import { NewClientPayload, LinkpassPrefill, ServiceRecipientFormValues, NextOfKinFormValues } from 'model';

// HELPERS
import { toISODate } from 'helpers/date';

// Builds the client payload for POST /onboarding/sessions/{id}/complete - same field names as
// ServiceRecipientFormValues, except dateOfBirth is sent as a plain YYYY-MM-DD date rather than the
// form's ISO datetime string
export const toOnboardingClientPayload = (values: ServiceRecipientFormValues): NewClientPayload => ({
  fullName: values.fullName,
  dateOfBirth: toISODate(values.dateOfBirth),
  gender: values.gender,
  nric: values.nric,
  email: values.email,
  homeNo: values.homeNo,
  mobileNo: values.mobileNo,
  maritalStatus: values.maritalStatus,
  residentialStatus: values.residentialStatus,
  nationality: values.nationality,
  languagesSpoken: values.languagesSpoken,
  otherLanguage: values.otherLanguage,
  isNtucMember: values.isNtucMember,
  ntucMemberNo: values.ntucMemberNo
});

// Converts linkpassPrefill's [year, month, day] dob tuple (month 1-indexed) into the ISO string the
// date-of-birth form fields expect - mirrors the DatePicker's own onChange (value.toISOString())
export const toLinkpassDateOfBirth = (dob?: LinkpassPrefill['dob']): string => {
  if (!dob) {
    return '';
  }

  const [year, month, day] = dob;

  return new Date(year, month - 1, day).toISOString();
};

// Maps linkpassPrefill (from POST /onboarding/sessions/{id}/role) onto the fields SelfDetailsForm
// and ServiceRecipientForm share - only name/dob/nric/email/mobile are known this early, the rest
// stay at the form's own defaults
export const toLinkpassPrefillValues = (prefill?: LinkpassPrefill): Partial<ServiceRecipientFormValues> => ({
  fullName: prefill?.name ?? '',
  dateOfBirth: toLinkpassDateOfBirth(prefill?.dob),
  nric: prefill?.nric ?? '',
  email: prefill?.email ?? '',
  mobileNo: prefill?.mobile ?? ''
});

// Maps linkpassPrefill onto NextOfKinForm's fields - for NOK-role sessions, linkpassPrefill is the
// logged-in NOK's own identity (see NokDetailsResponse), not the service recipient's, so it prefills
// NextOfKinForm rather than ServiceRecipientForm
export const toLinkpassNextOfKinPrefillValues = (prefill?: LinkpassPrefill): Partial<NextOfKinFormValues> => ({
  name: prefill?.name ?? '',
  email: prefill?.email ?? '',
  mobileNo: prefill?.mobile ?? ''
});

const normalizeContactValue = (value?: string): string => (value ?? '').trim().toLowerCase();

// Only flags a mismatch when linkpassPrefill actually returned a value for that field - an absent
// registered value must never manufacture a false-positive mismatch
const isFieldMismatched = (registeredValue?: string, enteredValue?: string): boolean => {
  const registeredNormalized = normalizeContactValue(registeredValue);

  return Boolean(registeredNormalized) && registeredNormalized !== normalizeContactValue(enteredValue);
};

export interface MismatchedFields {
  email: boolean;
  mobile: boolean;
}

// Reports which individual field(s) differ from the Linkpass-registered identity (linkpassPrefill,
// from POST /onboarding/sessions/{id}/role) - drives which MismatchedDetailsModal variant is shown
export const getMismatchedFields = (
  entered: Pick<ServiceRecipientFormValues, 'email' | 'mobileNo'>,
  registered?: LinkpassPrefill
): MismatchedFields => ({
  email: isFieldMismatched(registered?.email, entered.email),
  mobile: isFieldMismatched(registered?.mobile, entered.mobileNo)
});

// True when the submitted email/mobile differs from the Linkpass-registered identity - gates whether
// MismatchedDetailsModal is shown before completeSelfOnboarding/completeNOKOnboarding
export const hasMismatchedContactDetails = (
  entered: Pick<ServiceRecipientFormValues, 'email' | 'mobileNo'>,
  registered?: LinkpassPrefill
): boolean => {
  const { email, mobile } = getMismatchedFields(entered, registered);

  return email || mobile;
};
